import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { JSX } from "react";

import { useCan } from "../../../auth/permissions.js";
import { marketingApi } from "../client.js";
import type { AgentSettings } from "../types.js";
import { minutesAgo } from "../shared.js";

/**
 * Header button + modal dialog for steering the marketing agent.
 *
 * Replaces the inline "Steer the agent" textarea that used to live
 * in the Workbench banner. The steering string is appended to every
 * subsequent agent LLM prompt as additional context, so a one-line
 * note like "lean into Avaya migration this week, no pricing claims"
 * influences every generated draft until the operator clears it.
 *
 * Mirrors the Sales `SteerAgentButton` UX — `marketing:edit` gate,
 * modal dialog with a textarea + Save / Clear / Cancel actions, and
 * a small "last set X minutes ago" footer.
 */
export function MarketingSteerButton(): JSX.Element | null {
  const canEdit = useCan("marketing:edit");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const settings = useQuery({
    queryKey: ["marketing", "agent-settings"],
    queryFn: marketingApi.getAgentSettings,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  // Sync local edit buffer to whatever the server most recently
  // returned (and reset whenever the dialog opens).
  useEffect(() => {
    if (open) setDraft(settings.data?.steer_prompt ?? "");
  }, [open, settings.data?.steer_prompt]);

  const patch = useMutation({
    mutationFn: marketingApi.patchAgentSettings,
    onSuccess: (next: AgentSettings) => {
      qc.setQueryData(["marketing", "agent-settings"], next);
      setOpen(false);
    },
  });

  if (!canEdit) return null;

  const current = settings.data?.steer_prompt ?? null;
  const trimmed = draft.trim();
  const dirty = trimmed !== (current ?? "");
  const busy = patch.isPending;

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        color="inherit"
        size="small"
        onClick={() => setOpen(true)}
        // Box the emoji to a 16px square (like the sibling Pause/Resume
        // button's 16px MUI icon) so its taller glyph metrics can't inflate
        // the button height — the same trick the Sales SteerAgentButton uses.
        startIcon={
          <Box
            component="span"
            aria-hidden
            sx={{ display: "inline-flex", fontSize: 16, lineHeight: 1 }}
          >
            🎯
          </Box>
        }
        title="Drop a steering note — folded into every agent prompt until cleared"
        aria-label="Steer the marketing agent"
        // Match the Sales header buttons exactly (size="small" + px:1.5 / py:0.625
        // for the outlined variant — 5px so the 1px border lands at the same outer
        // height as the contained Pause/Resume's py:0.75).
        sx={{ fontSize: 12, fontWeight: 600, px: 1.5, py: 0.625 }}
      >
        Steer the agent
        {current ? (
          <Box
            aria-hidden
            title="Steering currently active"
            sx={{
              ml: 1,
              height: 6,
              width: 6,
              borderRadius: "50%",
              bgcolor: "primary.main",
            }}
          />
        ) : null}
      </Button>

      <Dialog
        open={open}
        onClose={() => !busy && setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Steer the marketing agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This note is appended to every agent LLM prompt until you clear it.
            Examples: "Lean into Avaya migration stories", "No pricing claims
            this week", "Focus on the SBC layer narrative".
          </DialogContentText>

          <Box component="label" sx={{ mt: 2, display: "grid", gap: 0.75, fontSize: 12 }}>
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.secondary",
              }}
            >
              Steering note
            </Typography>
            <TextField
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              placeholder="Tell the agent what to focus on…"
              disabled={busy}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.secondary",
              }}
            >
              <span>{trimmed.length}/1000</span>
              {settings.data?.updated_at ? (
                <span>
                  last set {minutesAgo(settings.data.updated_at)}m ago
                  {settings.data.updated_by ? ` · ${settings.data.updated_by}` : ""}
                </span>
              ) : null}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          {current ? (
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={() => patch.mutate({ clear_steer: true })}
              disabled={busy}
            >
              Clear steering
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={() => setOpen(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={() => patch.mutate({ steer_prompt: trimmed })}
            disabled={!dirty || busy}
          >
            {busy ? "Saving…" : "Save steering"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
